import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { CompanyEquipmentData } from "../../types/companyEquipments.type";
import useCompanyEquipmentDisable from "../../hooks/useCompanyEquipmentDisable";
import useCompanyEquipmentEnable from "../../hooks/useCompanyEquipmentEnable";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { companyEquipmentDisableSchema, CompanyEquipmentDisableValidationSchema } from "../../schemas/equipmentActions.schema";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";

const DisableEnableEquipment = ({ equipment }: { equipment: CompanyEquipmentData; }) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [disableModalIsOpen, setDisableModalIsOpen] = useState(false);
  const [enableModalIsOpen, setEnableModalIsOpen] = useState(false);

  const { mutate: disableEquipment } = useCompanyEquipmentDisable({
    equipmentId: equipment.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyEquipment', equipment.id] });
      reset();
      setDisableModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { mutate: enableEquipment } = useCompanyEquipmentEnable({
    equipmentId: equipment.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyEquipment', equipment.id] });
      setEnableModalIsOpen(false);
    },
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isDisabled = equipment.status === 'disabled';

  const {
    register,
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyEquipmentDisableValidationSchema>({
    resolver: zodResolver(companyEquipmentDisableSchema),
  });

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isDisabled && 'bg-[#FCCDCA33]')}>
          <div className="flex items-center gap-x-2">
            <InfoIcon strokeWidth={1} className="text-yellow-600" />

            <div>
              <p className="font-bold text-[10px] leading-3">
                {!isDisabled ? (
                  'Desativar o equipamento funciona assim como o bloqueio mas permite que outra empresa cadastre esse mesmo equipamento.'
                ) : (
                  'Reative o equipamento para permitir que todas as transações sejam realizadas.'
                )}
              </p>

              <p className="font-light text-[10px] italic leading-3">
                {!isDisabled ? (
                  'Se o equipamento for desativado, apenas a Tcard poderá reativar'
                ) : (
                  'Apenas a Tcard pode reativar um equipamento.'
                )}
              </p>
            </div>
          </div>

          {!isDisabled ? (
            <Modal
              Trigger={(
                <Button operationKlassToCheckPermission="Admin::Client::Equipments::Disable" type="button" variant="error">
                  Desativar equipamento
                </Button>
              )}
              title="Você tem certeza?"
              subtitle="Se o equipamento for desativado, apenas a Tcard poderá reativar."
              subtitleClassName="mt-1 text-white-300"
              showClose={false}
              useCancellableFooter
              isOpen={disableModalIsOpen}
              setIsOpen={setDisableModalIsOpen}
              onConfirm={handleSubmit((data) => disableEquipment(data))}
              body={(
                <form className="flex flex-col gap-4">
                  <TextField
                    className="text-white-800 opacity-80"
                    label="Motivo"
                    {...register('disable_reason')}
                    errorMessage={errors.disable_reason?.message || undefined}
                  />
                </form>
              )}
            />
          ) : (
            <Modal
              Trigger={(
                <Button operationKlassToCheckPermission="Admin::Client::Equipments::Enable" type="button">
                  Reativar equipamento
                </Button>
              )}
              title="Você tem certeza?"
              subtitle="Se o equipamento for reativado, todas as transações poderão ser realizadas."
              subtitleClassName="mt-2 text-white-300"
              confirmButtonClassName="bg-blue-700"
              showClose={false}
              useCancellableFooter
              isOpen={enableModalIsOpen}
              setIsOpen={setEnableModalIsOpen}
              onConfirm={() => enableEquipment()}
            />
          )}
        </div>
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default DisableEnableEquipment;
